import { useLayoutEffect, useSyncExternalStore } from 'react';

/**
 * Полноэкранный («иммерсивный») режим приложения. Слой, которому нужен весь экран (окно звонка, чат и
 * карточка на телефоне, галерея, модальное окно на телефоне), заявляет об этом через `useImmersive`, а
 * каркас (`AppShell`) сам перестаёт рисовать шапку, нижнее меню и боковое меню и обнуляет их высоты
 * (`--header-h`, `--nav-h`). Никаких CSS-заплаток с `display: none` по селекторам: что не нарисовано —
 * перекрыть ничего не может.
 *
 * `mobile` — только на телефоне (до `md`), `all` — везде, в том числе на компьютере.
 */
export type ImmersiveScope = 'mobile' | 'all';

export interface ImmersiveState {
  readonly mobile: boolean;
  readonly all: boolean;
}

const holders = new Map<symbol, ImmersiveScope>();
const listeners = new Set<() => void>();
let snapshot: ImmersiveState = { mobile: false, all: false };

function publish(): void {
  const scopes = [...holders.values()];
  const all = scopes.includes('all');
  const mobile = all || scopes.includes('mobile');
  if (snapshot.all === all && snapshot.mobile === mobile) return;
  snapshot = { mobile, all };
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    if (all) root.dataset.immersive = 'all';
    else if (mobile) root.dataset.immersive = 'mobile';
    else delete root.dataset.immersive;
  }
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const read = (): ImmersiveState => snapshot;
const IDLE: ImmersiveState = { mobile: false, all: false };

/** Заявить полноэкранный режим, пока `active`. Заявки складываются: режим держится, пока есть хоть одна. */
export function useImmersive(active: boolean, scope: ImmersiveScope = 'mobile'): void {
  useLayoutEffect(() => {
    if (!active) return undefined;
    const key = Symbol(scope);
    holders.set(key, scope);
    publish();
    return () => {
      holders.delete(key);
      publish();
    };
  }, [active, scope]);
}

/** Текущий режим: каркас прячет по нему шапку и меню. */
export function useImmersiveState(): ImmersiveState {
  return useSyncExternalStore(subscribe, read, () => IDLE);
}
